import type React from "react";
import { useState, useRef, useEffect } from "react";
import { Search, Info, Plus, ImageIcon, Smile, Send, Filter, Mic, X, Menu } from "lucide-react";
import Picker from "@emoji-mart/react";
import data from "@emoji-mart/data";

interface Contact {
    id: number;
    firstName: string;
    lastName: string;
    avatar: string;
    lastMessage: string;
    time: string;
    isOnline?: boolean;
    unreadCount?: number;
    role: string;
    mobile: string;
    email: string;
}

interface Message {
    id: number;
    sender: string;
    text: string;
    time: string;
    type?: "text" | "link" | "image" | "voice" | "file";
    callType?: "outgoing" | "missed" | "incoming";
}

const themes = {
    green: {
        primary: "bg-green-600 dark:bg-gray-800",
        hover: "hover:bg-green-700 dark:hover:bg-gray-700",
        accent: "text-green-600 dark:text-gray-300",
        bg: "bg-green-50 dark:bg-gray-900",
        border: "border-green-200 dark:border-gray-700",
        light: "bg-green-100 dark:bg-gray-800",
        text: "text-gray-800 dark:text-gray-200",
        secondaryBg: "bg-white dark:bg-gray-800",
        highlight: "bg-yellow-200 dark:bg-yellow-600"
    },
    blue: {
        primary: "bg-blue-600 dark:bg-gray-800",
        hover: "hover:bg-blue-700 dark:hover:bg-gray-700",
        accent: "text-blue-600 dark:text-gray-300",
        bg: "bg-blue-50 dark:bg-gray-900",
        border: "border-blue-200 dark:border-gray-700",
        light: "bg-blue-100 dark:bg-gray-800",
        text: "text-gray-800 dark:text-gray-200",
        secondaryBg: "bg-white dark:bg-gray-800",
        highlight: "bg-yellow-200 dark:bg-yellow-600"
    },
    red: {
        primary: "bg-red-600 dark:bg-gray-800",
        hover: "hover:bg-red-700 dark:hover:bg-gray-700",
        accent: "text-red-600 dark:text-gray-300",
        bg: "bg-red-50 dark:bg-gray-900",
        border: "border-red-200 dark:border-gray-700",
        light: "bg-red-100 dark:bg-gray-800",
        text: "text-gray-800 dark:text-gray-200",
        secondaryBg: "bg-white dark:bg-gray-800",
        highlight: "bg-yellow-200 dark:bg-yellow-600"
    },
    dark: {
        primary: "bg-gray-800",
        hover: "hover:bg-gray-700",
        accent: "text-gray-300",
        bg: "bg-gray-900",
        border: "border-gray-700",
        light: "bg-gray-800",
        text: "text-gray-200",
        secondaryBg: "bg-gray-800",
        highlight: "bg-yellow-600"
    },
};

const Chat = () => {
    const [contacts] = useState<Contact[]>([
        {
            id: 1,
            firstName: "Jasmine",
            lastName: "Thompson",
            avatar: "/placeholder.svg?height=32&width=32",
            lastMessage: "Hey, how's the project going?",
            time: "45 min",
            isOnline: true,
            role: "Project Manager",
            mobile: "+1 (555) 123-4567",
            email: "jasmine.thompson@email.com",
        },
        {
            id: 2,
            firstName: "Michael",
            lastName: "Chen",
            avatar: "/placeholder.svg?height=32&width=32",
            lastMessage: "Can we meet tomorrow?",
            time: "1 hr",
            isOnline: false,
            unreadCount: 2,
            role: "Developer",
            mobile: "+1 (555) 234-5678",
            email: "michael.chen@email.com",
        },
        {
            id: 3,
            firstName: "Sarah",
            lastName: "Williams",
            avatar: "/placeholder.svg?height=32&width=32",
            lastMessage: "Check out this design!",
            time: "2 hrs",
            isOnline: true,
            role: "Designer",
            mobile: "+1 (555) 345-6789",
            email: "sarah.williams@email.com",
        },
        {
            id: 4,
            firstName: "David",
            lastName: "Brown",
            avatar: "/placeholder.svg?height=32&width=32",
            lastMessage: "I'll send the report soon",
            time: "3 hrs",
            isOnline: false,
            unreadCount: 1,
            role: "Analyst",
            mobile: "+1 (555) 456-7890",
            email: "david.brown@email.com",
        },
        {
            id: 5,
            firstName: "Emma",
            lastName: "Davis",
            avatar: "/placeholder.svg?height=32&width=32",
            lastMessage: "Lunch tomorrow?",
            time: "4 hrs",
            isOnline: true,
            role: "Marketing",
            mobile: "+1 (555) 567-8901",
            email: "emma.davis@email.com",
        },
        {
            id: 6,
            firstName: "James",
            lastName: "Wilson",
            avatar: "/placeholder.svg?height=32&width=32",
            lastMessage: "Code review needed",
            time: "5 hrs",
            isOnline: false,
            role: "Senior Developer",
            mobile: "+1 (555) 678-9012",
            email: "james.wilson@email.com",
        },
    ]);

    const [messages, setMessages] = useState<Message[]>([
        {
            id: 1,
            sender: "Jasmine Thompson",
            text: "https://www.envato.com/atomic-power-plant-engine/",
            time: "May 10, 2022, 11:14 AM",
            type: "link",
        },
        {
            id: 2,
            sender: "You",
            text: "Looks interesting! I'll check it out.",
            time: "May 10, 2022, 11:15 AM",
            type: "text",
        },
    ]);

    const [inputMessage, setInputMessage] = useState("");
    const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [chatSearchQuery, setChatSearchQuery] = useState("");
    const [theme, setTheme] = useState<keyof typeof themes>("green");
    const [showFileMenu, setShowFileMenu] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showChatSearch, setShowChatSearch] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
            setTheme(e.matches ? 'dark' : 'green');
        };
        setTheme(mediaQuery.matches ? 'dark' : 'green');
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        return () => {
            if (mediaRecorderRef.current && isRecording) {
                mediaRecorderRef.current.stop();
            }
        };
    }, [isRecording]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            audioChunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };

            mediaRecorderRef.current.onstop = () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
                const newMessage: Message = {
                    id: messages.length + 1,
                    sender: "You",
                    text: URL.createObjectURL(audioBlob),
                    time: new Date().toLocaleString(),
                    type: "voice",
                };
                setMessages([...messages, newMessage]);
                setIsRecording(false);
            };

            mediaRecorderRef.current.start();
            setIsRecording(true);
        } catch (err) {
            console.error("Error accessing microphone:", err);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
        }
    };

    const sendMessage = (e: React.FormEvent) => {
        e.preventDefault();
        if (inputMessage.trim()) {
            const newMessage: Message = {
                id: messages.length + 1,
                sender: "You",
                text: inputMessage,
                time: new Date().toLocaleString(),
                type: "text",
            };
            setMessages([...messages, newMessage]);
            setInputMessage("");
        }
        if (isRecording) {
            stopRecording();
        }
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const fileType = file.type.startsWith('image/') ? 'image' : 'file';
            const newMessage: Message = {
                id: messages.length + 1,
                sender: "You",
                text: URL.createObjectURL(file),
                time: new Date().toLocaleString(),
                type: fileType,
            };
            setMessages([...messages, newMessage]);
            setShowFileMenu(false);
        }
    };

    const handleEmojiSelect = (emoji: any) => {
        setInputMessage(inputMessage + emoji.native);
        setShowEmojiPicker(false);
    };

    const closeAllPopups = () => {
        setShowFileMenu(false);
        setShowEmojiPicker(false);
        setShowProfileModal(false);
        setShowChatSearch(false);
    };

    const handleOutsideClick = (e: React.MouseEvent) => {
        if (isSidebarOpen && window.innerWidth < 768) {
            setIsSidebarOpen(false);
        }
        closeAllPopups();
    };

    const filteredContacts = contacts.filter((contact) =>
        `${contact.firstName} ${contact.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredMessages = messages.filter((msg) =>
        msg.text.toLowerCase().includes(chatSearchQuery.toLowerCase())
    );

    const handleContactSelect = (contact: Contact) => {
        setSelectedContact(contact);
        setMessages([
            {
                id: 1,
                sender: contact.firstName + " " + contact.lastName,
                text: contact.lastMessage,
                time: contact.time,
                type: "text",
            },
        ]);
        setIsSidebarOpen(false);
        closeAllPopups();
    };

    const highlightText = (text: string, query: string) => {
        if (!query) return text;
        const regex = new RegExp(`(${query})`, 'gi');
        return text.split(regex).map((part, index) =>
            regex.test(part) ? (
                <span key={index} className={themes[theme].highlight}>{part}</span>
            ) : (
                part
            )
        );
    };

    const renderMessage = (msg: Message) => {
        const isUser = msg.sender === "You";
        return (
            <div key={msg.id} className={`flex ${isUser ? "justify-end" : "justify-start"} mb-1 w-full`}>
                {!isUser && (
                    <div className="w-5 h-5 mr-1 mt-1 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center text-[10px] font-medium text-gray-600 dark:text-gray-200">
                        {msg.sender[0]}
                    </div>
                )}
                <div
                    className={`max-w-[70%] px-2 py-1 rounded-lg shadow-sm ${isUser
                        ? `${themes[theme].primary} text-white rounded-br-sm`
                        : `${themes[theme].secondaryBg} ${themes[theme].text} rounded-bl-sm border ${themes[theme].border}`
                        }`}
                >
                    {msg.type === "link" ? (
                        <a
                            href={msg.text}
                            className={`${isUser ? "text-blue-200" : "text-blue-600 dark:text-blue-300"} underline break-all text-[10px]`}
                        >
                            {highlightText(msg.text, chatSearchQuery)}
                        </a>
                    ) : msg.type === "image" ? (
                        <img src={msg.text} alt="Shared image" className="max-w-[150px] rounded-lg" />
                    ) : msg.type === "voice" ? (
                        <audio controls src={msg.text} className="max-w-[150px]" />
                    ) : msg.type === "file" ? (
                        <a
                            href={msg.text}
                            download
                            className={`${isUser ? "text-blue-200" : "text-blue-600 dark:text-blue-300"} underline break-all text-[10px]`}
                        >
                            {highlightText("Download File", chatSearchQuery)}
                        </a>
                    ) : (
                        <p className="text-[10px]">{highlightText(msg.text, chatSearchQuery)}</p>
                    )}
                    <p className={`text-[8px] mt-0.5 ${isUser ? "text-gray-200" : "text-gray-500 dark:text-gray-400"}`}>{msg.time}</p>
                </div>
            </div>
        );
    };

    return (
        <div
            className={`flex h-screen md:h-[600px] ${themes[theme].bg} font-sans text-xs transition-colors duration-300 overflow-hidden rounded-lg shadow-lg relative`}
            onClick={handleOutsideClick}
        >
            {/* Sidebar */}
            <div
                className={`fixed md:static inset-y-0 left-0 w-60 ${themes[theme].secondaryBg} border-r ${themes[theme].border} flex flex-col shadow-md transform ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"
                    } md:translate-x-0 transition-transform duration-300 z-20`}
            >
                {/* Theme Switcher */}
                <div className={`p-2 border-b ${themes[theme].border} flex justify-end space-x-1 bg-gradient-to-r from-${themes[theme].secondaryBg} to-${themes[theme].light}`}>
                    {Object.keys(themes).filter(t => t !== 'dark').map((t) => (
                        <button
                            key={t}
                            onClick={(e) => { e.stopPropagation(); setTheme(t as keyof typeof themes); }}
                            className={`w-5 h-5 rounded-full ${themes[t as keyof typeof themes].primary} ${theme === t ? "ring-1 ring-offset-1 ring-gray-400 dark:ring-gray-500" : ""
                                } transition-transform hover:scale-105`}
                        />
                    ))}
                </div>

                {/* Sidebar Header */}
                <div className={`p-2 border-b ${themes[theme].border}`}>
                    <div className="flex items-center justify-between mb-1">
                        <h1 className={`text-sm font-bold ${themes[theme].text}`}>Messages</h1>
                        <div className="flex space-x-1">
                            <button className={`p-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${themes[theme].hover}`} onClick={(e) => { e.stopPropagation(); }}>
                                <Plus className="w-3 h-3" />
                            </button>
                            <button className={`p-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${themes[theme].hover}`} onClick={(e) => { e.stopPropagation(); }}>
                                <Filter className="w-3 h-3" />
                            </button>
                        </div>
                    </div>

                    <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3 h-3" />
                        <input
                            type="text"
                            placeholder="Search contacts..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className={`w-full pl-7 pr-2 py-1 bg-gray-100 dark:bg-gray-700 border-none rounded-full focus:outline-none focus:ring-1 focus:ring-${themes[theme].primary} transition-all text-[10px] ${themes[theme].text}`}
                        />
                    </div>
                </div>

                {/* Contact List */}
                <div className="flex-1 overflow-y-auto">
                    {filteredContacts.map((contact) => (
                        <div
                            key={contact.id}
                            onClick={(e) => { e.stopPropagation(); handleContactSelect(contact); }}
                            className={`flex items-center p-2 hover:${themes[theme].light} cursor-pointer border-b ${themes[theme].border} ${selectedContact?.id === contact.id ? `${themes[theme].light} border-l-2 border-l-${themes[theme].primary}` : ""
                                } transition-all duration-200`}
                        >
                            <div className="relative">
                                <div className={`w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center text-[10px] font-medium text-gray-600 dark:text-gray-200`}>
                                    {contact.firstName[0]}
                                </div>
                                {contact.isOnline && (
                                    <div className="absolute bottom-0 right-0 w-1.5 h-1.5 bg-green-500 rounded-full border border-white dark:border-gray-800"></div>
                                )}
                            </div>
                            <div className="ml-2 flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                    <h3 className={`text-[10px] font-semibold ${themes[theme].text} truncate`}>{contact.firstName} {contact.lastName}</h3>
                                    <span className="text-[8px] text-gray-500 dark:text-gray-400">{contact.time}</span>
                                </div>
                                <p className="text-[8px] text-gray-500 dark:text-gray-400 truncate">{contact.lastMessage}</p>
                            </div>
                            {contact.unreadCount && (
                                <div className={`${themes[theme].primary} text-white rounded-full w-3 h-3 flex items-center justify-center text-[8px] font-medium`}>
                                    {contact.unreadCount}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Main Chat Area */}
            <div className={`flex-1 flex flex-col md:flex ${themes[theme].bg}`}>
                {/* Chat Header */}
                <div className={`bg-${themes[theme].secondaryBg} border-b ${themes[theme].border} p-2 flex items-center justify-between shadow-sm`}>
                    <div className="flex items-center">
                        <button
                            className="md:hidden p-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                            onClick={(e) => { e.stopPropagation(); setIsSidebarOpen(true); }}
                        >
                            <Menu className="w-4 h-4" />
                        </button>
                        {selectedContact && (
                            <>
                                <div
                                    className={`w-6 h-6 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center text-[10px] font-medium text-gray-600 dark:text-gray-200 cursor-pointer hover:ring-1 hover:ring-${themes[theme].primary} transition-all`}
                                    onClick={(e) => { e.stopPropagation(); setShowProfileModal(true); }}
                                >
                                    {selectedContact?.firstName[0] || "U"}
                                </div>
                                <div className="ml-2">
                                    <h2 className={`text-xs font-semibold ${themes[theme].text}`}>{selectedContact?.firstName} {selectedContact?.lastName}</h2>
                                    <p className={`text-[10px] ${themes[theme].accent}`}>{selectedContact?.isOnline ? "Online" : "Offline"}</p>
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex space-x-1">
                        <button
                            className={`p-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${themes[theme].hover}`}
                            onClick={(e) => { e.stopPropagation(); setShowChatSearch(!showChatSearch); }}
                        >
                            <Search className="w-3 h-3" />
                        </button>
                        <button className={`p-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors ${themes[theme].hover}`} onClick={(e) => e.stopPropagation()}>
                            <Info className="w-3 h-3" />
                        </button>
                    </div>
                </div>

                {/* Chat Search Input */}
                {showChatSearch && (
                    <div className={`p-2 bg-${themes[theme].secondaryBg} border-b ${themes[theme].border}`}>
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-3 h-3" />
                            <input
                                type="text"
                                placeholder="Search messages..."
                                value={chatSearchQuery}
                                onChange={(e) => setChatSearchQuery(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                className={`w-full pl-7 pr-2 py-1 bg-gray-100 dark:bg-gray-700 border-none rounded-full focus:outline-none focus:ring-1 focus:ring-${themes[theme].primary} transition-all text-[10px] ${themes[theme].text}`}
                            />
                        </div>
                    </div>
                )}

                {/* Messages Area */}
                <div className={`flex-1 ${window.innerWidth < 768 ? 'overflow-y-hidden' : 'overflow-y-auto'} p-2 bg-gradient-to-b from-${themes[theme].bg} to-${themes[theme].secondaryBg}`}>
                    {selectedContact ? (
                        <div className="w-full flex flex-col h-full">
                            {filteredMessages.map((msg) => renderMessage(msg))}
                            <div ref={messagesEndRef} />
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full">
                            <p className={`text-[10px] ${themes[theme].text}`}>Select a contact to start chatting</p>
                        </div>
                    )}
                </div>

                {/* Message Input */}
                {selectedContact && (
                    <div className={`bg-${themes[theme].secondaryBg} border-t ${themes[theme].border} p-2 shadow-sm sticky bottom-0 z-10`}>
                        <div className="flex items-center space-x-1">
                            <div className="relative">
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowFileMenu(!showFileMenu); }}
                                    className={`p-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors ${themes[theme].hover}`}
                                >
                                    <Plus className="w-3 h-3" />
                                </button>
                                {showFileMenu && (
                                    <div className={`absolute bottom-8 left-0 bg-${themes[theme].secondaryBg} border ${themes[theme].border} rounded-lg shadow-xl p-1 z-10`} onClick={(e) => e.stopPropagation()}>
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            className={`flex items-center p-1 text-[10px] text-gray-600 dark:text-gray-300 hover:${themes[theme].light} rounded w-full transition-colors`}
                                        >
                                            <ImageIcon className="w-3 h-3 mr-1" />
                                            File
                                        </button>
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            onChange={handleFileUpload}
                                            accept="*/*"
                                            className="hidden"
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="relative">
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowEmojiPicker(!showEmojiPicker); }}
                                    className={`p-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors ${themes[theme].hover}`}
                                >
                                    <Smile className="w-3 h-3" />
                                </button>
                                {showEmojiPicker && (
                                    <div className="absolute bottom-8 left-0 z-10" onClick={(e) => e.stopPropagation()}>
                                        <Picker data={data} onEmojiSelect={handleEmojiSelect} previewPosition="none" />
                                    </div>
                                )}
                            </div>

                            <form onSubmit={sendMessage} className="flex-1 flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
                                <input
                                    type="text"
                                    value={inputMessage}
                                    onChange={(e) => setInputMessage(e.target.value)}
                                    placeholder="Type a message..."
                                    className={`flex-1 px-2 py-1 border ${themes[theme].border} rounded-full focus:outline-none focus:ring-1 focus:ring-${themes[theme].primary} bg-gray-100 dark:bg-gray-700 transition-all text-[10px] ${themes[theme].text}`}
                                />
                                <button
                                    type="button"
                                    onClick={isRecording ? stopRecording : startRecording}
                                    className={`p-1 relative ${isRecording ? themes[theme].primary : "text-gray-600 dark:text-gray-300"} hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors`}
                                >
                                    <Mic className={`w-3 h-3 ${isRecording ? "text-white" : ""}`} />
                                    {isRecording && (
                                        <span className="absolute -top-0.5 -right-0.5 w-1 h-1 bg-red-500 rounded-full animate-pulse"></span>
                                    )}
                                </button>
                                <button
                                    type="submit"
                                    className={`px-2 py-1 ${themes[theme].primary} ${themes[theme].hover} text-white rounded-full flex items-center transition-all`}
                                >
                                    <Send className="w-3 h-3" />
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </div>

            {/* Profile Modal */}
            {showProfileModal && selectedContact && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowProfileModal(false);
                    }}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-xl p-3 w-72 max-w-[90%] mx-4 shadow-xl opacity-100 transform scale-100 transition-all duration-300"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-3">
                            <h2 className={`text-sm font-bold ${themes[theme].text}`}>Profile</h2>
                            <button
                                onClick={() => setShowProfileModal(false)}
                                className={`p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors ${themes[theme].hover}`}
                            >
                                <X className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                            </button>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 rounded-full bg-gray-300 dark:bg-gray-700 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-200 mb-2">
                                {selectedContact?.firstName[0] || "U"}
                            </div>
                            <h3 className={`text-sm font-semibold ${themes[theme].text}`}>
                                {selectedContact?.firstName} {selectedContact?.lastName}
                            </h3>
                            <p className={`text-[10px] ${themes[theme].accent} mb-2`}>
                                {selectedContact?.isOnline ? "Online" : "Offline"}
                            </p>
                            <div className="w-full space-y-1">
                                <div>
                                    <p className="text-[10px] font-medium text-gray-600 dark:text-gray-400">Role</p>
                                    <p className={`text-[10px] ${themes[theme].text}`}>{selectedContact?.role}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-medium text-gray-600 dark:text-gray-400">Mobile Number</p>
                                    <p className={`text-[10px] ${themes[theme].text}`}>{selectedContact?.mobile}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-medium text-gray-600 dark:text-gray-400">Email</p>
                                    <p className={`text-[10px] ${themes[theme].text}`}>{selectedContact?.email}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-medium text-gray-600 dark:text-gray-400">Last Message</p>
                                    <p className={`text-[10px] ${themes[theme].text} truncate`}>{selectedContact?.lastMessage}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-medium text-gray-600 dark:text-gray-400">Last Seen</p>
                                    <p className={`text-[10px] ${themes[theme].text}`}>{selectedContact?.time}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Chat;